import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { getAuth, signOut, signInWithCustomToken } from "firebase/auth";
import { firebaseApp } from "../libs/firebase";
import { AuthContext } from "./Auth";
import { getSearchApiBaseUrl } from "ui";
import { setImpersonationContext } from "../libs/analyticsManager";
import * as Sentry from "@sentry/react";

interface ImpersonationState {
  isImpersonating: boolean;
  actorUid: string | null;
  actorEmail: string | null;
  impersonatedUid: string | null;
  impersonatedEmail: string | null;
  exitImpersonation: () => Promise<void>;
}

const defaultState: ImpersonationState = {
  isImpersonating: false,
  actorUid: null,
  actorEmail: null,
  impersonatedUid: null,
  impersonatedEmail: null,
  exitImpersonation: async () => {},
};

export const ImpersonationCtx = createContext<ImpersonationState>(defaultState);

export function useImpersonation() {
  return useContext(ImpersonationCtx);
}

export function ImpersonationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { currentUser } = useContext(AuthContext) as any;
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [actorUid, setActorUid] = useState<string | null>(null);
  const [actorEmail, setActorEmail] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  // Read custom claims when user changes
  useEffect(() => {
    if (!currentUser) {
      setIsImpersonating(false);
      setActorUid(null);
      setActorEmail(null);
      return;
    }

    // getIdTokenResult returns claims from the token
    currentUser
      .getIdTokenResult()
      .then((result: any) => {
        const claims = result.claims || {};
        if (claims.impersonation) {
          setIsImpersonating(true);
          setActorUid(claims.actor_uid || null);
          setActorEmail(claims.actor_email || null);
          setExpiresAt(
            typeof claims.impersonation_expires_at === "number"
              ? claims.impersonation_expires_at
              : null,
          );
        } else {
          setIsImpersonating(false);
          setActorUid(null);
          setActorEmail(null);
          setExpiresAt(null);
        }
      })
      .catch((err: any) => {
        console.error("[ImpersonationProvider] getIdTokenResult error:", err);
        setIsImpersonating(false);
      });
  }, [currentUser]);

  // Sync impersonation state to analytics module + Sentry
  useEffect(() => {
    if (isImpersonating) {
      setImpersonationContext({
        is_impersonation: true,
        impersonation_actor_uid: actorUid,
        impersonation_actor_email: actorEmail,
        impersonated_uid: currentUser?.uid || null,
      });
      Sentry.setTag("impersonation", "true");
      Sentry.setTag("impersonation_actor", actorEmail);
    } else {
      setImpersonationContext({});
      Sentry.setTag("impersonation", null);
      Sentry.setTag("impersonation_actor", null);
    }
  }, [isImpersonating, actorUid, actorEmail, currentUser]);

  const exitImpersonation = useCallback(async () => {
    const auth = getAuth(firebaseApp);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        const apiUrl = getSearchApiBaseUrl();
        const resp = await fetch(`${apiUrl}/api/impersonation/end`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await resp.json();

        if (data.restoreToken) {
          // Restore the staff member's own session
          await signInWithCustomToken(auth, data.restoreToken);
          window.location.assign("/home");
          return;
        }
      }
    } catch (err) {
      console.warn("[Impersonation] end call failed, falling back to sign-out:", err);
    }

    // Fallback: sign out and go to root
    await signOut(auth);
    window.location.assign("/");
  }, []);

  // Auto-exit when impersonation session expires
  const exitRef = useRef(exitImpersonation);
  exitRef.current = exitImpersonation;
  useEffect(() => {
    if (!isImpersonating || !expiresAt) return;

    const check = () => {
      if (Date.now() >= expiresAt) {
        console.warn("[Impersonation] Session expired, auto-exiting");
        exitRef.current();
      }
    };

    check(); // immediate check on mount/refresh
    const interval = setInterval(check, 60_000);
    return () => clearInterval(interval);
  }, [isImpersonating, expiresAt]);

  const value = useMemo(
    () => ({
      isImpersonating,
      actorUid,
      actorEmail,
      impersonatedUid: currentUser?.uid || null,
      impersonatedEmail: currentUser?.email || null,
      exitImpersonation,
    }),
    [isImpersonating, actorUid, actorEmail, currentUser, exitImpersonation],
  );

  return (
    <ImpersonationCtx.Provider value={value}>
      {children}
    </ImpersonationCtx.Provider>
  );
}
